<template lang="pug">
b-modal#gamifyLoginOperatorUpdate(
  ref="gamifyLoginOperatorUpdate"
  :title="$t('ui.daily_login_update')"
  centered
  no-fade
  no-close-on-backdrop
  :busy="loading"
  @ok="done"
  @show="reset"
  @shown="shown"
)
  b-form(novalidate :disabled="!feedback.success" @submit.stop.prevent="submit")
    b-form-group(:label="$t('ui.id')" disabled).mb-0
      DebounceInput(
        size="sm"
        ref="id"
        type="text"
        v-model="gamify_id.toUpperCase()"
        :disabled="loading"
      )
    b-form-group(:label="$t('ui.cycle')").mb-0
      b-form-select(
        size="sm"
        :dirty="$v.cycle.$dirty"
        :error="$v.cycle.$error"
        v-model.trim.lazy="$v.cycle.$model"
        :disabled="loading"
      )
        option(v-for="i in 5" :value="i + 2") {{ i + 2 }}
      ValidateResult(:value="$v.cycle" :name="$t('ui.cycle')" :fvalue="'0'")
  .clusterize-scroll.table-responsive
    table.table.table-sm.table-bordered.table-align-middle
      thead.thead-light
        tr
          th.text-center(width="20%") {{ $tc('ui.day', 1) }}
          th.text-center {{ $t('ui.token') }}
          th.text-center {{ $t('ui.cash') }}
      tbody
        tr(v-for="x in Number(this.cycle)")
          td.text-center {{ x }}
          td
            div.d-flex.justify-content-center.align-items-center
              DebounceInput.text-center(
                size="sm"
                :ref="'token_' + x"
                required
                type="number"
                :dirty="$v.formToken['token_' + x].$dirty"
                :error="$v.formToken['token_' + x].$error"
                v-model="formToken['token_' + x]"
                :disabled="loading"
                keyPressRestrict="restrictOnlyNumber"
                @input="$v.formToken['token_' + x].$touch()"
              )
          td
            div.d-flex.justify-content-center.align-items-center
              DebounceInput.text-center(
                size="sm"
                :ref="'cash_' + x"
                required
                type="number"
                :dirty="$v.formCash['cash_' + x].$dirty"
                :error="$v.formCash['cash_' + x].$error"
                v-model="formCash['cash_' + x]"
                :disabled="loading"
                @input="$v.formCash['cash_' + x].$touch()"
              )
        tr(v-if="hasTokenErrors || hasCashErrors")
          td
          td(colspan="2")
            ul.text-danger.small.mb-0
              li(v-for="(val, key) in $v.formToken" v-if="val.$error") {{ $t('error.tokenInvalid', { key }) }}
              li(v-for="(val, key) in $v.formCash" v-if="val.$error") {{ $t('error.cashInvalid', { key }) }}

  b-form-group.mb-0(
    label-cols="3"
    label-align="left"
    label-size="sm"
    :label="$t('ui.payout_method')")
    PayoutMethod(
      ref="payout_method"
      size="sm"
      required
      type="array"
      :dirty="$v.payout_method.$dirty"
      :error="$v.payout_method.$error"
      v-model.trim.lazy="$v.payout_method.$model"
      :disabled="loading"
    )
    ValidateResult(:value="$v.payout_method" :name="$t('ui.payout_method')")

  b-form-group.mb-0(
    v-if="['INBOX'].includes(payout_method)"
    label-cols="3"
    label-align="left"
    label-size="sm"
    :label="$t('ui.campaign_id')")
    CampaignLookup(
      size="sm"
      ref="campaign_id"
      required
      type="array"
      :dirty="$v.campaign_id.$dirty"
      :error="$v.campaign_id.$error"
      v-model.trim.lazy="$v.campaign_id.$model"
      :disabled="loading"
      :operatorID="operatorID"
    )
    ValidateResult(:value="$v.campaign_id" :name="$t('ui.campaign_id')")

  div(slot="modal-cancel")
    SubmitButton(:loading="loading" icon="fa fa-ban" :text="$t('ui.cancel')" isModal=true)
  div(slot="modal-ok")
    SubmitButton(:loading="loading" icon="fa fa-save" :text="$t('ui.save')" isModal=true)
</template>

<script>
import { required, integer, requiredIf } from "vuelidate/lib/validators";
import config from "@/config";
import errorTags from "@/errorTags";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";
import PayoutMethod from "@/library/PayoutMethod";
import CampaignLookup from "@/library/CampaignLookup";

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput,
    PayoutMethod,
    CampaignLookup
  },
  props: {
    item: {
      type: Object,
      default: null
    },
    operatorID: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      feedback: {
        success: true,
        status: ""
      },
      gamify_id: '',
      cycle: 1,
      formToken: {},
      formCash: {},
      payout_method: "",
      campaign_id: ""
    };
  },
  validations() {
    const formTokenValidations = {};
    const formCashValidations = {};
    for (let i = 1; i <= Number(this.cycle); i++) {
      formTokenValidations[`token_${i}`] = {
        required,
        mustMoreThanOrEqual: function(value) {
          if(parseFloat(value) < 0) return false
          return true
        }
      };
    }
    for (let i = 1; i <= Number(this.cycle); i++) {
      formCashValidations[`cash_${i}`] = {
        required,
        mustMoreThanOrEqual: function(value) {
          if(parseFloat(value) < 0) return false
          return true
        }
      };
    }
    return {
      formToken: formTokenValidations,
      formCash: formCashValidations,
      cycle: {
        integer,
        mustMoreThanOrEqual: function(value) {
          if(parseFloat(value) < 0) return false
          return true
        }
      },
      payout_method: {
        required
      },
      campaign_id: {
        required: requiredIf(function () {
          return ['INBOX'].includes(this.payout_method);
        })
      }
    }
  },
  computed: {
    hasTokenErrors() {
      return Object.values(this.$v.formToken).some(v => v.$error);
    },
    hasCashErrors() {
      return Object.values(this.$v.formCash).some(v => v.$error);
    }
  },
  methods: {
    done: function(evt) {
      evt.preventDefault();
      this.submit();
    },
    submit: function() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        let url = config.gamifyLoginOperatorUpdateUrl();

        var json = {
          action_by: this.$store.getters.loginId,
          auth_token: this.$store.getters.sessionToken,
          operator_id: this.operatorID,
          gamify_id: this.item.gamify_id,
          cycle: this.cycle,
          payout_method: this.payout_method,
          campaign_id: ['INBOX'].includes(this.payout_method) ? this.campaign_id : ""
        };

        for(var x = 1; x <= this.cycle; x++) {
          json['token_' + x] = this.formToken['token_' + x]
          json['cash_' + x] = this.formCash['cash_' + x]
        }

        this.feedback.success = true;
        this.loading = true;
        this.$http.post(url, json).then(
          function(response) {
            this.loading = false;
            // Success
            try {
              if (response) {
                if (typeof response.data.status == "string") {
                  this.feedback.success = response.data.status == "1";
                } else {
                  this.feedback.success = response.data.status == 1;
                }
                this.feedback.status = response.data.statusdesc;
                if (this.feedback.success == true) {
                  this.$emit("clicked");
                  this.$refs.gamifyLoginOperatorUpdate.hide();
                } else {
                  this.$helpers.handleFeedback(this.feedback.status);
                  if(response.data.status == -9) {
                    this.$emit("failed", "daily_login_update", response.data.statusdesc);
                  } else {
                    this.$emit("failed", "daily_login_update", this.feedback.status);
                  }
                  this.$refs.gamifyLoginOperatorUpdate.hide();
                }
              }
            } catch (error) {
              this.feedback.success = false;
              this.feedback.status = errorTags.request.failed;
            }
          },
          function() {
            this.loading = false;
            // Error
            this.feedback.success = false;
            this.feedback.status = errorTags.request.failed;
          }
        );
      }
    },
    reset: function() {
      this.feedback.success = true;
      this.feedback.status = "";
    },
    shown: function() {
      this.gamify_id = this.item.gamify_id;
      this.cycle = this.item.cycle
      this.payout_method = this.item.payout_method;
      if(['INBOX'].includes(this.payout_method)) this.campaign_id = this.item.campaign_id
      else this.campaign_id = "";
      if(this.cycle < 3) this.cycle = 3

      // if add keys dynamically after formToken / formCash is already reactive, Vue won't detect them unless you use $set
      const tokens = {};
      const cashs = {};
      for(var x = 1; x <= this.cycle; x++) {
        this.$set(tokens, `token_${x}`, this.item['token_' + x]);
        this.$set(cashs, `cash_${x}`, this.item['cash_' + x]);
      }
      this.formToken = tokens;
      this.formCash = cashs;
      this.$v.$reset();
      this.$v.$touch();
    }
  }
};
</script>